import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Scale, TrendingDown, Activity, Bell, Droplets, Utensils, Dumbbell, Moon, Plus, Calendar, Target, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, AreaChart, Area } from 'recharts';
import { parseLocalDate, formatDateBR, getTodayString, isToday } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [recentProgress, setRecentProgress] = useState([]);
  const [evolutionTable, setEvolutionTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickLogForm, setQuickLogForm] = useState({
    peso: '',
    agua_ml: '',
    atividade_fisica: '',
    horas_sono: ''
  });
  const [showQuickLog, setShowQuickLog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Adicionar timestamp para evitar cache
      const timestamp = Date.now();
      const headers = { 
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      };
      
      const [statsRes, notifsRes, progressRes, evolutionRes] = await Promise.all([
        axios.get(`${API_URL}/api/progress/stats?_t=${timestamp}`, { headers }),
        axios.get(`${API_URL}/api/notifications?_t=${timestamp}`, { headers }),
        axios.get(`${API_URL}/api/progress?_t=${timestamp}`, { headers }),
        axios.get(`${API_URL}/api/patient/evolution?_t=${timestamp}`, { headers }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setNotifications(notifsRes.data.filter(n => !n.lida).slice(0, 5));
      setRecentProgress(progressRes.data.slice(0, 7).reverse());
      
      // Build evolution table based on user's treatment duration
      const duration = user?.duracao_tratamento || 6;
      const totalDoses = duration * 4;
      const existingData = evolutionRes.data || [];
      const table = [];
      for (let i = 1; i <= totalDoses; i++) {
        const existing = existingData.find(e => e.dose_num === i) || {};
        table.push({
          dose_num: i,
          dose_mg: existing.dose_mg || '',
          data_aplicacao: existing.data_aplicacao || '',
          peso: existing.peso || '',
          med_quadril: existing.med_quadril || '',
          med_abdome: existing.med_abdome || ''
        });
      }
      setEvolutionTable(table);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user?.duracao_tratamento]);

  useEffect(() => {
    fetchData();
    
    // Recarregar dados quando a página fica visível novamente
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
        if (refreshUser) refreshUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Recarregar dados a cada 30 segundos quando a página está ativa
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [fetchData, refreshUser]);

  const handleQuickLog = async (e) => {
    e.preventDefault();
    try {
      // Usar data local para evitar problema de timezone
      const now = new Date();
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const data = {
        data: localDate,
        peso: quickLogForm.peso ? parseFloat(quickLogForm.peso) : null,
        agua_ml: quickLogForm.agua_ml ? parseInt(quickLogForm.agua_ml) : null,
        atividade_fisica: quickLogForm.atividade_fisica || null,
        horas_sono: quickLogForm.horas_sono ? parseFloat(quickLogForm.horas_sono) : null
      };
      await axios.post(`${API_URL}/api/progress/daily`, data);
      toast.success('Registro salvo com sucesso!');
      setShowQuickLog(false);
      setQuickLogForm({ peso: '', agua_ml: '', atividade_fisica: '', horas_sono: '' });
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar registro');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  const calcularIMCCategoria = (imc) => {
    if (imc < 18.5) return { texto: 'Abaixo do peso', cor: 'text-blue-600' };
    if (imc < 25) return { texto: 'Peso normal', cor: 'text-green-600' };
    if (imc < 30) return { texto: 'Sobrepeso', cor: 'text-yellow-600' };
    return { texto: 'Obesidade', cor: 'text-red-600' };
  };

  // Função para formatar data sem problemas de timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`;
    }
    return dateStr;
  };

  const imcCategoria = calcularIMCCategoria(stats?.imc || 0);

  // Preparar dados para gráficos
  const pesoData = recentProgress.filter(p => p.peso).map(p => ({
    data: formatDate(p.data),
    peso: p.peso
  }));

  const progressPercentage = Math.min(Math.max(stats?.percentual_evolucao || 0, 0), 100);
  const pieData = [
    { name: 'Concluído', value: progressPercentage },
    { name: 'Restante', value: 100 - progressPercentage }
  ];

  const COLORS = ['#B8977A', '#E5E5E5'];

  // Calcular streak (dias consecutivos)
  const calculateStreak = () => {
    let streak = 0;
    const todayStr = getTodayString();
    const today = parseLocalDate(todayStr);
    for (let i = 0; i < recentProgress.length; i++) {
      const date = parseLocalDate(recentProgress[i].data);
      if (!date) continue;
      const diff = Math.floor((today - date) / (1000 * 60 * 60 * 24));
      if (diff === i) streak++;
      else break;
    }
    return streak;
  };

  const streak = calculateStreak();

  // Dados de hoje
  const todayProgress = recentProgress.find(p => isToday(p.data));

  return (
    <Layout>
      <div className="space-y-5 sm:space-y-6">
        {/* Header com saudação */}
        <div className="floating-card-lg bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] p-5 sm:p-8 text-white" style={{ background: 'linear-gradient(135deg, #0F2B4C 0%, #B8977A 100%)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">
                Olá, {user?.nome?.split(' ')[0]}! 👋
              </h2>
              <p className="text-white/90 text-sm sm:text-base md:text-lg">
                Continue sua jornada de emagrecimento
              </p>
            </div>
            {streak > 0 && (
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 sm:p-6 text-center self-start sm:self-auto border border-white/20">
                <p className="text-3xl sm:text-5xl font-bold mb-1">{streak}</p>
                <p className="text-xs sm:text-sm opacity-90">dias consecutivos</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards Principais */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {/* Card 1: Peso Inicial */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-peso-inicial">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <Scale className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                Início
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              Peso Inicial
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {stats?.peso_inicial?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">kg</p>
          </div>

          {/* Card 2: Peso Atual */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-peso-atual">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Scale className="text-[#0F2B4C]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                -{((stats?.peso_inicial || 0) - (stats?.peso_atual || 0)).toFixed(1)}kg
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              Peso Atual
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {stats?.peso_atual?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">kg</p>
          </div>

          {/* Card 3: Meta de Peso */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-peso-meta">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <Target className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                Meta
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              Meta de Peso
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {stats?.peso_meta?.toFixed(1) || '--'}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">kg</p>
          </div>

          {/* Card 4: IMC */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-imc">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Activity className="text-[#0F2B4C]" size={18} />
              </div>
              <span className={`text-[10px] sm:text-xs px-2.5 py-1 rounded-full font-medium ${imcCategoria.cor} bg-opacity-10`}>
                {imcCategoria.texto}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              IMC
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {stats?.imc || '--'}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">Índice de Massa</p>
          </div>

          {/* Card 5: Evolução */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-evolucao">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <TrendingDown className="text-[#B8977A]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-[#B8977A]/15 text-[#7A5E42] px-2.5 py-1 rounded-full font-medium">
                {stats?.percentual_evolucao?.toFixed(0) || '0'}%
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              Evolução
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {stats?.percentual_evolucao?.toFixed(0) || '0'}%
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">do objetivo</p>
          </div>

          {/* Card 6: Falta */}
          <div className="floating-card p-4 sm:p-6 float-transition" data-testid="card-falta">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <Target className="text-[#0F2B4C]" size={18} />
              </div>
              <span className="text-[10px] sm:text-xs bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full font-medium">
                Restante
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1 font-medium">
              Falta
            </p>
            <p className="text-2xl sm:text-4xl font-bold text-highlight mb-1">
              {((stats?.peso_atual || 0) - (stats?.peso_meta || 0)).toFixed(1)}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">kg para meta</p>
          </div>
        </div>

        {/* Progresso Visual e Gráfico */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Progress Chart */}
          {pesoData.length > 0 && (
            <div className="floating-card p-5 sm:p-6">
              <h3 className="text-base sm:text-xl font-bold mb-4 text-highlight">
                Evolução Semanal
              </h3>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={pesoData}>
                  <defs>
                    <linearGradient id="colorPeso" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#B8977A" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#B8977A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="data" 
                    stroke="#737373"
                    style={{ fontSize: '10px' }}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    stroke="#737373"
                    style={{ fontSize: '10px' }}
                    tick={{ fontSize: 10 }}
                    width={35}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="peso" 
                    stroke="#B8977A" 
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorPeso)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Progress Pie */}
          <div className="floating-card p-5 sm:p-6">
            <h3 className="text-base sm:text-xl font-bold mb-4 text-highlight">
              Meta de Emagrecimento
            </h3>
            <div className="flex items-center justify-center">
              <div className="relative">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-bold text-highlight">
                      {progressPercentage.toFixed(0)}%
                    </p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">completo</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-xl">
                <p className="text-lg sm:text-2xl font-bold text-highlight">{stats?.peso_inicial || '--'}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Inicial</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-xl">
                <p className="text-lg sm:text-2xl font-bold text-highlight">{stats?.peso_meta || '--'}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Meta</p>
              </div>
            </div>
          </div>
        </div>

        {/* Registro Rápido de Hoje */}
        <div className="floating-card p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-xl font-bold text-highlight">
              Registro Rápido
            </h3>
            <button
              data-testid="toggle-quick-log"
              onClick={() => setShowQuickLog(!showQuickLog)}
              className="bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl p-2.5 hover:shadow-lg hover:shadow-[#0F2B4C]/30 transition-all"
            >
              <Plus size={18} />
            </button>
          </div>

          {showQuickLog ? (
            <form onSubmit={handleQuickLog} className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 text-muted-foreground">Peso (kg)</label>
                <input
                  data-testid="quick-peso"
                  type="number"
                  step="0.1"
                  value={quickLogForm.peso}
                  onChange={(e) => setQuickLogForm({ ...quickLogForm, peso: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 sm:h-12 px-3 sm:px-4 text-sm"
                  placeholder="70.5"
                />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 text-muted-foreground">Água (ml)</label>
                <input
                  data-testid="quick-agua"
                  type="number"
                  value={quickLogForm.agua_ml}
                  onChange={(e) => setQuickLogForm({ ...quickLogForm, agua_ml: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 sm:h-12 px-3 sm:px-4 text-sm"
                  placeholder="2000"
                />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 text-muted-foreground">Atividade</label>
                <input
                  data-testid="quick-atividade"
                  type="text"
                  value={quickLogForm.atividade_fisica}
                  onChange={(e) => setQuickLogForm({ ...quickLogForm, atividade_fisica: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 sm:h-12 px-3 sm:px-4 text-sm"
                  placeholder="Caminhada"
                />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 text-muted-foreground">Sono (h)</label>
                <input
                  data-testid="quick-sono"
                  type="number"
                  step="0.5"
                  value={quickLogForm.horas_sono}
                  onChange={(e) => setQuickLogForm({ ...quickLogForm, horas_sono: e.target.value })}
                  className="w-full bg-muted/50 border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 sm:h-12 px-3 sm:px-4 text-sm"
                  placeholder="8"
                />
              </div>
              <button
                data-testid="submit-quick-log"
                type="submit"
                className="col-span-2 md:col-span-4 bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl py-3 font-semibold hover:shadow-lg hover:shadow-[#0F2B4C]/30 transition-all text-sm sm:text-base"
              >
                Salvar Registro
              </button>
            </form>
          ) : todayProgress ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {todayProgress.peso && (
                <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
                  <Scale className="mx-auto mb-1 sm:mb-2 text-[#B8977A]" size={20} />
                  <p className="text-lg sm:text-2xl font-bold text-highlight">{todayProgress.peso}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">kg</p>
                </div>
              )}
              {todayProgress.agua_ml && (
                <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
                  <Droplets className="mx-auto mb-1 sm:mb-2 text-[#0F2B4C]" size={20} />
                  <p className="text-lg sm:text-2xl font-bold text-highlight">{todayProgress.agua_ml}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">ml</p>
                </div>
              )}
              {todayProgress.atividade_fisica && (
                <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
                  <Dumbbell className="mx-auto mb-1 sm:mb-2 text-[#B8977A]" size={20} />
                  <p className="text-xs sm:text-sm font-medium text-highlight truncate">{todayProgress.atividade_fisica}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">atividade</p>
                </div>
              )}
              {todayProgress.horas_sono && (
                <div className="bg-muted/50 rounded-xl p-3 sm:p-4 text-center">
                  <Moon className="mx-auto mb-1 sm:mb-2 text-[#0F2B4C]" size={20} />
                  <p className="text-lg sm:text-2xl font-bold text-highlight">{todayProgress.horas_sono}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">horas</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-6 sm:py-8 text-sm">
              Nenhum registro hoje. Clique no + para adicionar!
            </p>
          )}
        </div>

        {/* Notificações */}
        {notifications.length > 0 && (
          <div className="floating-card p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="text-[#B8977A]" size={18} />
              <h3 className="text-base sm:text-xl font-bold text-highlight">
                Notificações
              </h3>
              <span className="ml-auto bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white text-[10px] sm:text-xs px-3 py-1 rounded-full font-medium">
                {notifications.length}
              </span>
            </div>
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  data-testid={`notification-${notif.tipo}`}
                  className="p-4 bg-muted/50 rounded-xl hover:bg-muted/70 transition-all cursor-pointer"
                >
                  <p className="text-xs sm:text-sm font-medium">
                    {notif.mensagem}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    {formatDateBR(notif.created_at)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabela de Evolução / Aplicação - Somente Visualização */}
        {evolutionTable.length > 0 && (
          <div className="floating-card p-5 sm:p-6 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="text-[#B8977A]" size={20} />
              <h3 className="text-base sm:text-xl font-bold text-highlight">
                Tabela de Evolução / Aplicação
              </h3>
            </div>
            
            <div className="overflow-x-auto -mx-5 px-5 pb-2">
              <table className="w-full border-collapse text-sm" style={{ minWidth: 'max-content' }}>
                <thead>
                  <tr>
                    <th className="bg-gradient-to-r from-[#B8977A] to-[#A98B6B] text-white p-3 text-left text-xs sm:text-sm font-semibold rounded-tl-xl whitespace-nowrap">
                      Dados
                    </th>
                    {evolutionTable.map((item, index) => (
                      <th key={index} className={`bg-gradient-to-r from-[#B8977A] to-[#A98B6B] text-white p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap ${index === evolutionTable.length - 1 ? 'rounded-tr-xl' : ''}`}>
                        {index + 1}ª
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="bg-muted p-2 sm:p-3 text-xs sm:text-sm font-medium text-highlight whitespace-nowrap">Doses Mg</td>
                    {evolutionTable.map((item, index) => (
                      <td key={index} className="p-2 text-center text-xs sm:text-sm text-muted-foreground">
                        {item.dose_mg || '--'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-border">
                    <td className="bg-muted p-2 sm:p-3 text-xs sm:text-sm font-medium text-highlight whitespace-nowrap">Data</td>
                    {evolutionTable.map((item, index) => (
                      <td key={index} className="p-2 text-center text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                        {item.data_aplicacao ? formatDate(item.data_aplicacao) : '--'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-border">
                    <td className="bg-muted p-2 sm:p-3 text-xs sm:text-sm font-medium text-highlight whitespace-nowrap">Peso</td>
                    {evolutionTable.map((item, index) => (
                      <td key={index} className="p-2 text-center text-xs sm:text-sm text-muted-foreground">
                        {item.peso || '--'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-border">
                    <td className="bg-muted p-2 sm:p-3 text-xs sm:text-sm font-medium text-highlight whitespace-nowrap">Quadril</td>
                    {evolutionTable.map((item, index) => (
                      <td key={index} className="p-2 text-center text-xs sm:text-sm text-muted-foreground">
                        {item.med_quadril || '--'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="bg-muted p-2 sm:p-3 text-xs sm:text-sm font-medium text-highlight whitespace-nowrap rounded-bl-xl">Abdome</td>
                    {evolutionTable.map((item, index) => (
                      <td key={index} className="p-2 text-center text-xs sm:text-sm text-muted-foreground">
                        {item.med_abdome || '--'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-3">
              * Dados preenchidos pelo seu médico
            </p>
          </div>
        )}

        {/* Histórico Médico - Somente Visualização */}
        {user?.historico_medico && (
          <div className="floating-card p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <FileText className="text-[#0F2B4C]" size={20} />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-highlight">
                  Histórico Médico
                </h3>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  Informações registradas pelo seu médico
                </p>
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {user.historico_medico}
              </p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            data-testid="quick-action-progresso"
            onClick={() => navigate('/progresso')}
            className="floating-card-lg bg-gradient-to-br from-[#B8977A] to-[#9C7D5E] text-white p-5 sm:p-6 text-left hover:shadow-xl hover:scale-[1.02] transition-all border-0"
            style={{ background: 'linear-gradient(135deg, #B8977A 0%, #9C7D5E 100%)' }}
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <TrendingDown size={24} />
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-1">
              Ver Progresso
            </h3>
            <p className="text-sm opacity-90">
              Gráficos e histórico
            </p>
          </button>

          <button
            data-testid="quick-action-plano"
            onClick={() => navigate('/plano-alimentar')}
            className="floating-card-lg bg-gradient-to-br from-[#0F2B4C] to-[#1C3D66] text-white p-5 sm:p-6 text-left hover:shadow-xl hover:scale-[1.02] transition-all border-0"
            style={{ background: 'linear-gradient(135deg, #0F2B4C 0%, #1C3D66 100%)' }}
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <Utensils size={24} />
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-1">
              Plano Alimentar
            </h3>
            <p className="text-sm opacity-90">
              Refeições do dia
            </p>
          </button>

          <button
            data-testid="quick-action-receitas"
            onClick={() => navigate('/receitas')}
            className="floating-card-lg text-white p-5 sm:p-6 text-left hover:shadow-xl hover:scale-[1.02] transition-all border-0"
            style={{ background: 'linear-gradient(135deg, #B8977A 0%, #0F2B4C 100%)' }}
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <Calendar size={24} />
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-1">
              Minhas Receitas
            </h3>
            <p className="text-sm opacity-90">
              Prescrições
            </p>
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
